/**
 * Renders an `otpauth://` provisioning URI as a QR code.
 *
 * CLIENT-SIDE ONLY, deliberately. The URI contains the TOTP shared secret; the
 * API returns it exactly once (§5) and it is not stored in recoverable form.
 * Sending it to any QR service — or anywhere at all — would hand a staff member's
 * second factor to a third party. `qrcode` is bundled, and the resulting `data:`
 * URL is inside the panel's `img-src 'self' data:` CSP.
 */
import { useEffect, useState } from 'react';
import QRCode from 'qrcode';

export function QrCode({ value, size = 220 }: { value: string; size?: number }): React.JSX.Element {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void QRCode.toDataURL(value, { width: size, margin: 1, errorCorrectionLevel: 'M' })
      .then((url) => {
        if (!cancelled) setDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [value, size]);

  if (failed) {
    // Never a dead end: the URI itself can be pasted into the authenticator.
    return (
      <code className="block break-all rounded-chip bg-surface-100 p-3 text-xs text-ink-700">
        {value}
      </code>
    );
  }

  if (!dataUrl) return <div style={{ width: size, height: size }} className="bg-surface-100" />;

  return (
    <img
      src={dataUrl}
      width={size}
      height={size}
      alt=""
      className="rounded-chip border-1 border-line-200 bg-surface-0"
    />
  );
}
